import React, { useEffect, useState, useRef, useMemo } from 'react';
import { v4 as generateUUID } from '@lukeed/uuid';
import { useDispatch, useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { has, get, isEmpty, map, getOr, omit, set } from 'lodash/fp';
import {
  Button,
  ButtonToolbar,
  ControlLabel,
  Form,
  FormControl,
  Icon
} from 'rsuite';
import {
  getIsSaving,
  getIsSaved,
  getSavingError
} from '../../../redux/Guest/selectors/components/household-member-form';
import {
  createHouseholdMember,
  updateHouseholdMember
} from '../../../redux/Guest/sagas/routines';
import { fetchHouseholdMemberColumns } from '../../../redux/sagas/household-member-column/routines';
import { fetchSettings } from '../../../redux/Settings/sagas/main/routines';
import { getActiveColumnsByName } from '../../../redux/selectors/household-member-column';
import { getMainSettingsData } from '../../../redux/Settings/selectors/main';
import { resetHouseholdMemberForm } from '../../../redux/Guest/actions/components/household-member-form';
import fieldPropsByType from '../../../helpers/fieldPropsByType';
import { parseInt } from '../../../helpers/utils';
import { MemberType } from '../../../interfaces/guest';
import {
  dataToFormValue,
  formValueToBody,
  toFieldOptions
} from '../Form/settings';
import {
  DefaultValues,
  createModelSchema,
  showFieldsPerSettings,
  requiredFieldsPerSettings
} from './settings';
import { CompressFormGroup } from '../../widgets/FormElements';
import { StringOrNumberType } from '../../../interfaces';

const StyledControlLabel = styled(ControlLabel)`
  white-space: normal;
  width: 230px !important;
  font-size: 12px !important;
  text-align: left !important;
`;

const HouseholdMemberForm = ({
  data,
  guestId,
  onSaved,
  onClose,
  allowClose
}) => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const [formValue, setFormValue] = useState(DefaultValues);
  const [formError, setFormError] = useState({});
  const columnSettings = useSelector(getActiveColumnsByName);
  const settings = useSelector(getMainSettingsData);
  const isSaving = useSelector(getIsSaving);
  const saveError = useSelector(getSavingError);
  const isSaved = useSelector(getIsSaved);
  const isContactForHelp = parseInt(get('contactForHelp', formValue)) === 2;
  const showFields = showFieldsPerSettings(settings);
  const requiredFields = requiredFieldsPerSettings(settings, isContactForHelp);
  const isEditMode = !!get('id', formValue);

  const formFields = useMemo(() => omit('id', columnSettings), [
    columnSettings
  ]);

  const formModel = useMemo(
    () => createModelSchema({ formFields, requiredFields }),
    [formFields, requiredFields]
  );

  const handleFormChange = value => {
    setFormValue(value);
  };

  const handleSubmit = () => {
    if (formRef.current.check()) {
      const id = get('id', formValue);
      const body = formValueToBody(formValue, formFields);
      if (!id) {
        const payload = { guestId, body: set('id', generateUUID(), body) };
        dispatch(createHouseholdMember(payload));
      } else dispatch(updateHouseholdMember({ id, guestId, body }));
    } else {
      toastr.error('Oops!', 'Unable to save. Please check form error.');
    }
  };

  const handleFormCheck = errors => {
    setFormError(errors);
  };

  useEffect(
    () => {
      if (isEmpty(settings)) dispatch(fetchSettings());
    },
    [settings]
  );

  useEffect(
    () => {
      if (isEmpty(formFields)) {
        dispatch(fetchHouseholdMemberColumns());
        return;
      }
      if (isEmpty(data)) return;
      setFormValue(dataToFormValue(data, formFields));
    },
    [data, formFields]
  );

  useEffect(
    () => {
      if (isSaved && isEmpty(saveError)) {
        onSaved();
        dispatch(resetHouseholdMemberForm());
      }
    },
    [isSaved, saveError]
  );

  return (
    <Form
      ref={formRef}
      layout="horizontal"
      model={formModel}
      formValue={formValue}
      onCheck={handleFormCheck}
      onChange={handleFormChange}
      style={{ overflow: 'hidden' }}
    >
      {map(({ name, label, fieldType, isRequired, options = [] }) => {
        const showField = getOr(true, name, showFields);
        if (!showField || !label) return '';

        const requiredField = getOr(isRequired, name, requiredFields);
        const hasError = has(name, formError);
        const fieldProps = getOr(() => ({}), fieldType, fieldPropsByType)({
          options: toFieldOptions(options)
        });

        return (
          <CompressFormGroup
            key={`field-${name}`}
            className={hasError && 'error'}
          >
            <StyledControlLabel>
              {label} {requiredField && <span style={{ color: 'red' }}>*</span>}
            </StyledControlLabel>
            <FormControl
              {...fieldProps}
              name={name}
              errorPlacement="rightStart"
            />
          </CompressFormGroup>
        );
      }, formFields)}
      <ButtonToolbar style={{ textAlign: 'right' }}>
        {(isEditMode || allowClose) && (
          <Button onClick={onClose} disabled={isSaving}>
            <Icon icon="close-circle" /> Cancel
          </Button>
        )}
        <Button appearance="primary" onClick={handleSubmit} disabled={isSaving}>
          <Icon icon="save" />
          {isSaving ? ' Saving...' : ' Save'}
        </Button>
      </ButtonToolbar>
    </Form>
  );
};

HouseholdMemberForm.propTypes = {
  data: MemberType,
  guestId: StringOrNumberType.isRequired,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
  allowClose: PropTypes.bool
};

HouseholdMemberForm.defaultProps = {
  data: {},
  onClose: () => {},
  onSaved: () => {},
  allowClose: false
};

export default HouseholdMemberForm;
